import { Suspense } from 'react';
import { Route } from 'react-router-dom';
import { useCapabilities } from '../capabilities';
import { moduleRoutes } from './modules';
import { RouteDenied } from '../../components/ui';
import type { FeatureState } from '../api/me.gen';

// The host mounts a module's route only while at least one of its feature keys
// is live for this org (on, or locked-but-discoverable). Off/hidden → the route
// is not mounted and, being lazy, its code is never fetched. This is the
// "intersect with the capability document → mount surviving routes" step (P18
// §1); SSE-driven capability changes re-render this and add/remove routes live.
const moduleLive = (featureKeys: string[], featureState: (k: string) => FeatureState): boolean =>
  featureKeys.some((key) => {
    const fs = featureState(key);
    if (fs.state === 'on') return true;
    if (fs.state === 'locked') return fs.discovery?.mode !== 'hidden';
    return false;
  });

// Returns the <Route> elements for the requested chrome, ready to spread inside
// the shell's <Routes>. A hook (not a component) because it must run within the
// Router + CapabilityProvider that App already provides.
export function useModuleRoutes(chrome: 'shell' | 'full'): JSX.Element[] {
  const { featureState, hasPermission, isHydrated } = useCapabilities();
  return moduleRoutes()
    .filter((route) => (route.chrome ?? 'shell') === chrome)
    .map((route) => {
      const Element = route.element;
      const live = moduleLive(route.featureKeys, featureState);
      const permitted = !route.permission || hasPermission(route.permission);

      // Pre-hydration the capability doc hasn't loaded yet (permissions/flags
      // boot empty) — stay mounted on the REAL element so an ENTITLED
      // persona's hard load never flashes a denial (mirrors the
      // unknown-feature-defaults-on rule elsewhere). Once hydrated, a denied
      // persona sees the in-app RouteDenied surface (story 2-45) — reason
      // 'locked' when the feature itself isn't live for this org, 'permission'
      // when the route's `permission` (2-20's route ↔ nav-row alignment) is
      // missing — instead of unmounting to the SPA's `*` catch-all, which used
      // to bounce an authenticated user to the marketing landing page mid-session.
      let element: JSX.Element;
      if (isHydrated && !live) {
        element = <RouteDenied reason="locked" featureKey={route.featureKeys[0]} />;
      } else if (isHydrated && !permitted) {
        element = <RouteDenied reason="permission" />;
      } else {
        element = (
          <Suspense fallback={null}>
            <Element />
          </Suspense>
        );
      }

      return <Route key={`${route.moduleKey}:${route.path}`} path={route.path} element={element} />;
    });
}
